import {
  UniversalCamera,
  Scene,
  Vector3,
  Ray,
  Animation,
  AnimationGroup,
  Color3,
  RayHelper,
} from '@babylonjs/core';

/**
 * Grid-based maze controller for Babylon.js Camera using the Animation system.
 * 
 * Features:
 * - Uses Animation API with keyframes for smooth movement/rotation
 * - Automatic grid snapping after each movement
 * - Continuous movement while holding keys
 * - Collision detection via raycasting (0.75x tile size)
 * - Configurable linear and angular speeds
 * - Easing functions for smooth motion
 * - Movement locking for battle system (rotation still allowed)
 */
export class MazeCameraController {
  // Configuration
  private tileSize: number;
  private linearSpeed: number; // units per second
  private angularSpeed: number; // radians per second
  private showDebugRays: boolean;

  // Movement vectors for each cardinal direction (Babylon uses Y-up, Z-forward)
  private readonly directions = [
    new Vector3(0, 0, -1),  // Forward (negative Z in Babylon camera space)
    new Vector3(-1, 0, 0),  // Left (negative X)
    new Vector3(0, 0, 1),   // Backward (positive Z)
    new Vector3(1, 0, 0),   // Right (positive X)
  ];

  private readonly targetAngles = [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2];

  // State tracking
  private currentAngleIndex: number;
  private isMoving: boolean = false;
  private isRotating: boolean = false;
  private movementLocked: boolean = false;

  // Rays for collision detection
  private frontRay: Ray;
  private backRay: Ray;
  private frontRayHelper?: RayHelper;
  private backRayHelper?: RayHelper;

  // Reusable vector for forward direction derived from camera.rotation.y (avoids relying on currentAngleIndex)
  private readonly _forwardDir = new Vector3();

  // Animation references
  private currentAnimation?: AnimationGroup;
  private footstepTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Input callback (should return {x: rotation, y: forward/back})
  private getInput: () => { x: number; y: number };

  // Optional callback when player takes a step (for footstep SFX)
  private onFootstep: (() => void) | undefined;

  // Track previous input for continuous movement
  private prevRotationInput: number = 0;
  private prevMovementInput: number = 0;

  private camera: UniversalCamera;
  private scene: Scene;
  private frameRate: number = 60;

  private beforeRenderObserver: ReturnType<Scene['onBeforeRenderObservable']['add']> | null = null;

  constructor(
    camera: UniversalCamera,
    scene: Scene,
    getInputCallback: () => { x: number; y: number },
    {
      tileSize = 4,
      linearSpeed = 4,
      angularSpeed = Math.PI * 2, // 360 degrees per second
      initialRotation = 0,
      showDebugRays = false,
      onFootstep,
    }: {
      tileSize?: number;
      linearSpeed?: number;
      angularSpeed?: number;
      initialRotation?: number;
      showDebugRays?: boolean;
      onFootstep?: () => void;
    } = {}
  ) {
    this.camera = camera;
    this.scene = scene;
    this.getInput = getInputCallback;
    this.tileSize = tileSize;
    this.linearSpeed = linearSpeed;
    this.angularSpeed = angularSpeed;
    this.showDebugRays = showDebugRays;
    this.onFootstep = onFootstep;

    // Calculate initial angle index from rotation
    const normalized = ((initialRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    this.currentAngleIndex = Math.round(normalized / (Math.PI / 2)) % 4;

    // Set initial rotation and snap to grid
    this.camera.rotation.y = this.targetAngles[this.currentAngleIndex];
    this.camera.position = this.snapToGrid(this.camera.position);

    // Initialize rays (length must reach next tile center so we block before stepping into chests/obstacles)
    this.frontRay = new Ray(
      this.camera.position,
      this.directions[this.currentAngleIndex],
      this.tileSize
    );

    this.backRay = new Ray(
      this.camera.position.clone(),
      this.directions[this.currentAngleIndex].scale(-1),
      this.tileSize
    );

    // Debug visualization
    if (this.showDebugRays) {
      this.frontRayHelper = new RayHelper(this.frontRay);
      this.frontRayHelper.show(scene, Color3.Red());

      this.backRayHelper = new RayHelper(this.backRay);
      this.backRayHelper.show(scene, Color3.Green());
    }

    // Register update loop (store observer so we can remove it in dispose)
    this.beforeRenderObserver = scene.onBeforeRenderObservable.add(() => this.update());
  }

  /**
   * Lock or unlock player movement and rotation
   */
  public setMovementLocked(locked: boolean): void {
    this.movementLocked = locked;
  }

  /**
   * Check if the camera is currently rotating
   */
  public getIsRotating(): boolean {
    return this.isRotating;
  }

  private update(): void {
    // Get input state
    // x > 0 = CW, x < 0 = CCW
    // y > 0 = forward, y < 0 = backward
    const { x, y } = this.getInput();

    // Keep currentAngleIndex in sync with camera when idle (e.g. after interrupted rotation)
    if (!this.isRotating && !this.isMoving) {
      const angle = this.camera.rotation.y;
      const normalized = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
      this.currentAngleIndex = Math.round(normalized / (Math.PI / 2)) % 4;
    }

    // Update ray positions and directions
    this.updateRays();

    // Check for collisions
    const frontBlocked = this.checkCollision(this.frontRay);
    const backBlocked = this.checkCollision(this.backRay);

    // Determine actual movement input (blocked by walls OR movement lock)
    let movementInput = y;
    if (this.movementLocked) {
      movementInput = 0; // Disable all movement when locked
    } else {
      if (frontBlocked && y > 0) movementInput = 0;
      if (backBlocked && y < 0) movementInput = 0;
    }

    // Handle rotation input (always allowed, even during battle)
    this.handleRotation(x);

    // Handle linear movement input (blocked during battle)
    this.handleMovement(movementInput);
  }

  /** Forward direction from camera.rotation.y so movement/rays stay in sync even if currentAngleIndex is stale. */
  private getForwardDirection(): Vector3 {
    const r = this.camera.rotation.y;
    return this._forwardDir.set(-Math.sin(r), 0, -Math.cos(r));
  }

  private updateRays(): void {
    const forward = this.getForwardDirection();

    this.frontRay.origin.copyFrom(this.camera.position);
    this.frontRay.direction.copyFrom(forward);
    this.frontRay.length = this.tileSize;

    this.backRay.origin.copyFrom(this.camera.position);
    this.backRay.direction.copyFrom(forward);
    this.backRay.direction.scaleInPlace(-1);
    this.backRay.length = this.tileSize;

    // Update debug helpers
    if (this.showDebugRays) {
      if (this.frontRayHelper) {
        this.frontRayHelper.dispose();
        this.frontRayHelper = new RayHelper(this.frontRay);
        this.frontRayHelper.show(this.scene, Color3.Red());
      }

      if (this.backRayHelper) {
        this.backRayHelper.dispose();
        this.backRayHelper = new RayHelper(this.backRay);
        this.backRayHelper.show(this.scene, Color3.Green());
      }
    }
  }

  private handleRotation(input: number): void {
    const inputActive = input !== 0;

    // Block rotation if movement is locked (during battle)
    if (this.movementLocked) {
      this.prevRotationInput = 0;
      return;
    }

    if (!this.isRotating && inputActive && !this.isMoving) {
      this.startRotation(input);
    }

    this.prevRotationInput = input;
  }

  private startRotation(direction: number): void {
    // Calculate new angle index
    const rotationDirection = direction > 0 ? 1 : -1; // CW = 1, CCW = -1
    const newAngleIndex = (this.currentAngleIndex + rotationDirection + 4) % 4;
    const targetAngle = this.targetAngles[newAngleIndex];

    this.isRotating = true;

    // Calculate duration based on angular speed
    const angleDiff = Math.PI / 2; // Always 90 degrees
    const durationSeconds = angleDiff / this.angularSpeed;
    const totalFrames = Math.ceil(durationSeconds * this.frameRate);

    // Get current angle and determine shortest path to target
    const currentAngle = this.camera.rotation.y;
    let finalTargetAngle = targetAngle;

    // Handle wrapping to take shortest path
    const diff = targetAngle - currentAngle;
    if (diff > Math.PI) {
      // Target is ahead but wrapping back is shorter
      finalTargetAngle = targetAngle - 2 * Math.PI;
    } else if (diff < -Math.PI) {
      // Target is behind but wrapping forward is shorter
      finalTargetAngle = targetAngle + 2 * Math.PI;
    }

    // Create rotation animation
    const rotationAnim = new Animation(
      'mazeRotation',
      'rotation.y',
      this.frameRate,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Set keyframes
    rotationAnim.setKeys([
      { frame: 0, value: currentAngle },
      { frame: totalFrames, value: finalTargetAngle }
    ]);

    // Create animation group for better control
    const animGroup = new AnimationGroup('rotationGroup', this.scene);
    animGroup.addTargetedAnimation(rotationAnim, this.camera);

    // Play animation
    this.currentAnimation = animGroup;
    animGroup.play(false);

    // Handle completion
    animGroup.onAnimationGroupEndObservable.addOnce(() => {
      // Snap to exact angle (in case of overshoot) and normalize
      this.camera.rotation.y = targetAngle;
      this.currentAngleIndex = newAngleIndex;
      this.isRotating = false;
      this.currentAnimation = undefined;
      animGroup.dispose();

      // If rotation key is still held, start next rotation
      if (this.prevRotationInput !== 0 && !this.isMoving) {
        this.startRotation(this.prevRotationInput);
      }
    });
  }

  private handleMovement(input: number): void {
    const inputActive = input !== 0;

    if (!this.isMoving && inputActive && !this.isRotating) {
      this.startMovement(input);
    }

    this.prevMovementInput = input;
  }

  private startMovement(direction: number): void {
    this.onFootstep?.();
    // Use actual camera facing so movement matches view even if currentAngleIndex was desynced
    const movementDirection = direction > 0 ? 1 : -1;
    const movementVector = this.getForwardDirection().clone().scaleInPlace(movementDirection * this.tileSize);
    const targetPos = this.camera.position.add(movementVector);

    this.isMoving = true;

    // Calculate duration based on linear speed
    const durationSeconds = this.tileSize / this.linearSpeed;
    const totalFrames = Math.ceil(durationSeconds * this.frameRate);

    // Second footstep halfway through the step
    if (this.footstepTimeoutId !== null) clearTimeout(this.footstepTimeoutId);
    this.footstepTimeoutId = setTimeout(() => {
      this.footstepTimeoutId = null;
      this.onFootstep?.();
    }, (durationSeconds * 0.5) * 1000);

    // Create position animation
    const positionAnim = new Animation(
      'mazeMovement',
      'position',
      this.frameRate,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    // Set keyframes
    positionAnim.setKeys([
      { frame: 0, value: this.camera.position.clone() },
      { frame: totalFrames, value: targetPos }
    ]);

    // Create animation group
    const animGroup = new AnimationGroup('movementGroup', this.scene);
    animGroup.addTargetedAnimation(positionAnim, this.camera);

    // Play animation
    this.currentAnimation = animGroup;
    animGroup.play(false);

    // Handle completion
    animGroup.onAnimationGroupEndObservable.addOnce(() => {
      if (this.footstepTimeoutId !== null) {
        clearTimeout(this.footstepTimeoutId);
        this.footstepTimeoutId = null;
      }
      // Snap to grid (in case of overshoot)
      this.camera.position = this.snapToGrid(this.camera.position);
      this.isMoving = false;
      this.currentAnimation = undefined;
      animGroup.dispose();

      // If movement key is still held and not blocked, start next movement
      if (this.prevMovementInput !== 0 && !this.isRotating) {
        // Re-check collision AND movement lock before starting next movement
        if (this.movementLocked) {
          // Movement is locked, don't continue
          return;
        }

        const frontBlocked = this.checkCollision(this.frontRay);
        const backBlocked = this.checkCollision(this.backRay);

        const canMoveForward = this.prevMovementInput > 0 && !frontBlocked;
        const canMoveBackward = this.prevMovementInput < 0 && !backBlocked;

        if (canMoveForward || canMoveBackward) {
          this.startMovement(this.prevMovementInput);
        }
      }
    });
  }

  private checkCollision(ray: Ray): boolean {
    const pickInfo = this.scene.pickWithRay(ray, (mesh) => {
      return mesh.checkCollisions && mesh.isPickable;
    });

    if (!pickInfo?.hit) return false;

    // If the player is already overlapping the obstacle (e.g. walked into a chest or enemy tile),
    // don't treat the hit as blocking so the player can still move out.
    const MIN_BLOCKING_DISTANCE = this.tileSize * 0.3;
    return (pickInfo.distance ?? 0) >= MIN_BLOCKING_DISTANCE;
  }

  private snapToGrid(pos: Vector3): Vector3 {
    const halfTile = this.tileSize / 2;
    return new Vector3(
      Math.round(pos.x / halfTile) * halfTile,
      pos.y, // Don't snap Y (vertical axis)
      Math.round(pos.z / halfTile) * halfTile
    );
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this.footstepTimeoutId !== null) {
      clearTimeout(this.footstepTimeoutId);
      this.footstepTimeoutId = null;
    }
    if (this.beforeRenderObserver !== null) {
      this.scene.onBeforeRenderObservable.remove(this.beforeRenderObserver);
      this.beforeRenderObserver = null;
    }

    const anim = this.currentAnimation;
    this.currentAnimation = undefined;
    if (anim) {
      anim.stop();
      anim.dispose();
    }

    if (this.frontRayHelper) {
      this.frontRayHelper.dispose();
    }

    if (this.backRayHelper) {
      this.backRayHelper.dispose();
    }
  }
}